import styled, { css } from 'styled-components';

import Calendar from '../images/calendar.svg?react';
import Chat from '../images/chat.svg?react';
import Drag from '../images/dragHandle.svg?react';
import Tick from '../images/green_tick.svg?react';
import Grid from '../images/grid.svg?react';
import InfoOutline from '../images/info_outline.svg?react';
import List from '../images/list.svg?react';
import ClosedTrades from '../images/menu_closed_trades_icon.svg?react';
import CopiedKaptains from '../images/menu_copied_kaptains_icon.svg?react';
import Dashboard from '../images/menu_dashboard_icon.svg?react';
import Feed from '../images/menu_feed_icon.svg?react';
import Leaderboard from '../images/menu_leaderboard_icon.svg?react';
import Market from '../images/menu_markets_icon.svg?react';
import MostTraded from '../images/menu_most_traded_icon.svg?react';
import News from '../images/menu_news_icon.svg?react';
import OpenTrades from '../images/menu_open_trade_icon.svg?react';
import PendingTrades from '../images/menu_pending_trades_icon.svg?react';
import Portfolio from '../images/menu_portfolio_icon.svg?react';
import TopMovers from '../images/menu_top_movers_icon.svg?react';
import NavBell from '../images/nav_bell.svg?react';
import NavChat from '../images/nav_chat.svg?react';
import NavChevron from '../images/nav_chevron.svg?react';
import NavGear from '../images/nav_gear.svg?react';
import NavHelp from '../images/nav_help.svg?react';
import NavMenu from '../images/nav_menu.svg?react';
import NavSearch from '../images/nav_search.svg?react';
import Notification from '../images/notification.svg?react';
import Profile from '../images/profile.svg?react';
import Cross from '../images/red_cross.svg?react';
import Search from '../images/search.svg?react';
import { royal } from '../Theme';

const hoverColor = royal;
const liveHoverColor = '#c5abf5';

const svgFillActiveCss = css<{ $active: boolean }>`
  cursor: pointer;
  * {
    fill: ${props => (props.$active ? hoverColor : props.theme.subMenu.icon)};
  }
`;

const svgStrokeActiveCss = css<{ $active: boolean }>`
  cursor: pointer;
  * {
    stroke: ${props => (props.$active ? hoverColor : props.theme.subMenu.icon)};
  }
`;

const svgFillLiveCss = css<{ $active: boolean; $isLive: boolean }>`
  cursor: pointer;
  path {
    fill: ${props => (props.$active ? (props.$isLive ? props.theme.white : hoverColor) : props.$isLive ? liveHoverColor : '')};
  }
`;

const Wrapper = styled.div`
  position: relative;
  height: 1.625rem;
  width: 1.625rem;
`;

const Dot = styled.div`
  position: absolute;
  height: 0.5rem;
  width: 0.5rem;
  border-radius: 50%;
  background-color: ${props => props.theme.softRed};
  top: 0;
  right: 0.15rem;
  pointer-events: none;
`;

export const Pill = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -30%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.theme.coral};
  border: 2px solid ${props => props.theme.white};
  color: ${props => props.theme.white};
  font-size: 0.75rem;
  text-align: center;
  padding: 1px;
  line-height: 16px;
`;

const Bell = styled(Notification)<{ $active: boolean; $isLive: boolean }>`
  ${svgFillLiveCss}
`;

const ChatIcon = styled(Chat)`
  ${svgFillLiveCss}
`;

/**
 *  Choose one from hasMessage or count
 *  hasMessage will display red dot
 *  count will display message count
 */
const NotificationIcon = ({
  hasMessage = false,
  handleOnMouseEnter,
  count = 0,
  onClick,
  $active = false,
  $isLive = false,
}: {
  hasMessage?: boolean;
  count?: number;
  handleOnMouseEnter: () => void;
  onClick?: () => void;
  $active?: any;
  $isLive?: any;
}) => {
  return (
    <Wrapper onMouseEnter={handleOnMouseEnter} onClick={onClick}>
      {hasMessage && <Dot />}
      {count > 0 && <Pill>{count}</Pill>}
      <Bell $active={$active} $isLive={$isLive} />
    </Wrapper>
  );
};

const ProfileIcon = styled(Profile)`
  ${svgFillLiveCss}
`;

const Avatar = styled.div<{ avatarLink: string }>`
  width: 2.813rem;
  height: 2.813rem;
  border-radius: 50%;
  background-color: ${props => props.theme.gray};
  background-image: url(${props => props.avatarLink});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
`;

const GridIcon = styled(Grid)<{ $active: boolean }>`
  path {
    fill: ${props => props.$active && hoverColor};
  }

  :hover path {
    fill: ${hoverColor};
  }
`;

const ListIcon = styled(List)<{ $active: boolean }>`
  path {
    fill: ${props => props.$active && hoverColor};
  }

  :hover path {
    fill: ${hoverColor};
  }
`;

const DragHandle = styled(Drag)`
  cursor: pointer;
`;

const DashboardIcon = styled(Dashboard)`
  ${svgStrokeActiveCss}
`;

const NewsIcon = styled(News)`
  ${svgStrokeActiveCss}
`;

const FeedIcon = styled(Feed)`
  ${svgFillActiveCss}
`;

const LeaderboardIcon = styled(Leaderboard)`
  ${svgStrokeActiveCss}
`;

const CalendarIcon = styled(Calendar)`
  ${svgFillActiveCss}
`;

const MarketIcon = styled(Market)`
  ${svgStrokeActiveCss}
`;

const TopMoversIcon = styled(TopMovers)`
  ${svgStrokeActiveCss}
`;
const MostTradedIcon = styled(MostTraded)`
  ${svgStrokeActiveCss}
`;
const PortfolioIcon = styled(Portfolio)`
  ${svgFillActiveCss}
`;
const OpenTradesIcon = styled(OpenTrades)`
  ${svgFillActiveCss}
`;
const PendingTradesIcon = styled(PendingTrades)`
  ${svgFillActiveCss}
`;
const ClosedTradesIcon = styled(ClosedTrades)`
  ${svgFillActiveCss}
`;
const CopiedKaptainsIcon = styled(CopiedKaptains)`
  ${svgFillActiveCss}
`;
const SearchIcon = styled(Search)<{ $active: boolean; $isLive: boolean }>`
  ${svgFillLiveCss}
`;

const CrossIcon = styled(Cross)`
  cursor: pointer;
`;
const TickIcon = styled(Tick)`
  cursor: pointer;
`;

/** Outline info glyph; inherits `currentColor` from the consumer. */
const InfoOutlineIcon = styled(InfoOutline)``;

export {
  Avatar,
  CalendarIcon,
  ChatIcon,
  ClosedTradesIcon,
  CopiedKaptainsIcon,
  CrossIcon,
  DashboardIcon,
  DragHandle,
  FeedIcon,
  GridIcon,
  InfoOutlineIcon,
  LeaderboardIcon,
  ListIcon,
  MarketIcon,
  MostTradedIcon,
  NavBell,
  NavChat,
  NavChevron,
  NavGear,
  NavHelp,
  NavMenu,
  NavSearch,
  NewsIcon,
  NotificationIcon,
  OpenTradesIcon,
  PendingTradesIcon,
  PortfolioIcon,
  ProfileIcon,
  SearchIcon,
  TickIcon,
  TopMoversIcon,
};
